/*
 * 模块: MarketAnalysis
 * 职责: 市场ABA分析页面 (独立于SKU的数据分析)
 * 依赖: antd, react-router-dom, api
 * 导出: MarketAnalysis
 */

import React, { useState, useEffect } from 'react';
import { Table, Button, Upload, message, Card, Spin, Space, Tag, Modal, Typography, Empty } from 'antd';
import { UploadOutlined, BarChartOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { UploadProps, UploadChangeParam } from 'antd/es/upload';
import type { ColumnsType } from 'antd/es/table';
import { PageHeader } from '../../components/Layout/PageHeader';
import { periodAPI } from '../../services/api';
import type { Period } from '../../types';
import dayjs from 'dayjs';

const { Text } = Typography;

/**
 * 市场分析页面 - 展示不关联SKU的ABA数据
 */
export const MarketAnalysis: React.FC = () => {
    const navigate = useNavigate();
    const [periods, setPeriods] = useState<Period[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    // 加载市场数据 (无SKU关联的Period)
    const loadPeriods = async () => {
        setLoading(true);
        try {
            const response = await periodAPI.list({ sku: 'null' });
            const data = response.data;
            // 安全提取数组数据，处理分页响应
            if (Array.isArray(data)) {
                setPeriods(data);
            } else if (data && typeof data === 'object' && Array.isArray((data as any).results)) {
                setPeriods((data as any).results);
            } else {
                setPeriods([]);
            }
        } catch (error) {
            message.error('加载数据失败');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPeriods();
    }, []);

    // 上传配置
    const uploadProps: UploadProps = {
        name: 'files',
        action: '/api/periods/batch_upload/',
        data: {
            period_mode: 'auto',
            quick_mode: 'true',
        },
        multiple: true,
        accept: '.csv',
        showUploadList: false,
        withCredentials: true,
        beforeUpload: () => {
            setUploading(true);
            return true;
        },
        onChange: (info: UploadChangeParam) => {
            if (info.file.status === 'done') {
                message.success(`${info.file.name} 上传成功，后台处理中...`);
                setUploading(false);
                loadPeriods();
            } else if (info.file.status === 'error') {
                message.error(`${info.file.name} 上传失败`);
                setUploading(false);
            }
        },
    };

    // 删除周期
    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: '确认删除',
            content: '删除后该周期的所有关键词分析数据也将被删除，是否继续？',
            onOk: async () => {
                try {
                    await periodAPI.delete(id);
                    message.success('删除成功');
                    loadPeriods();
                } catch (error) {
                    message.error('删除失败');
                }
            },
        });
    };

    // 表格列定义
    const columns: ColumnsType<Period> = [
        {
            title: '周期',
            dataIndex: 'week_number',
            key: 'week_number',
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: '日期范围',
            key: 'date_range',
            render: (_, record) => (
                <Text type="secondary">
                    {dayjs(record.start_date).format('YYYY/MM/DD')} - {dayjs(record.end_date).format('MM/DD')}
                </Text>
            ),
        },
        {
            title: '关键词数',
            dataIndex: 'keywords_count',
            key: 'keywords_count',
            render: (count) => <Tag color="blue">{count ?? 0} 个</Tag>,
        },
        {
            title: '状态',
            dataIndex: 'processing_status',
            key: 'processing_status',
            render: (status) => {
                const statusMap: Record<string, { color: string; text: string }> = {
                    pending: { color: 'default', text: '待处理' },
                    processing: { color: 'processing', text: '处理中' },
                    completed: { color: 'success', text: '已完成' },
                    failed: { color: 'error', text: '失败' },
                };
                const { color, text } = statusMap[status] || { color: 'default', text: status };
                return <Tag color={color}>{text}</Tag>;
            },
        },
        {
            title: '上传时间',
            dataIndex: 'uploaded_at',
            key: 'uploaded_at',
            render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/aba-analysis/${record.id}`)}
                        disabled={record.processing_status !== 'completed'}
                    >
                        查看分析
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                    >
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <PageHeader
                title="ABA分析"
                subtitle="独立于SKU的市场大盘关键词分析"
                icon={<BarChartOutlined />}
                breadcrumbs={[
                    { title: '首页', href: '/' },
                    { title: 'ABA分析' },
                ]}
                actions={[
                    {
                        key: 'refresh',
                        label: '刷新',
                        type: 'default',
                        icon: <ReloadOutlined />,
                        onClick: loadPeriods,
                    },
                ]}
            />

            <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
                <Card
                    title="上传ABA数据"
                    style={{ marginBottom: 24 }}
                    extra={
                        <Upload {...uploadProps}>
                            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
                                上传CSV文件
                            </Button>
                        </Upload>
                    }
                >
                    <Text type="secondary">
                        上传Amazon Brand Analytics的热门搜索词CSV文件。数据将进行关键词分析，生成市场洞察报告。
                    </Text>
                </Card>

                <Card title="已上传数据">
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin />
                        </div>
                    ) : periods.length === 0 ? (
                        <Empty description="暂无市场分析数据，请上传ABA CSV文件" />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={periods}
                            rowKey="id"
                            pagination={{ pageSize: 10 }}
                        />
                    )}
                </Card>
            </div>
        </div>
    );
};

export default MarketAnalysis;
